import { TFunction } from 'i18next';
import { toLocaleDateTime } from 'utils/datetime';
import {
  getBranchRulesMarkdownReport,
  getBranchRulesRepositoryProblemText,
  GitHubBranchResult,
  GitHubBranchRule,
  GitHubBranchRuleResult,
  GitHubBranchRulesConfig,
  GitHubBranchRulesEvidence,
  GitHubBranchRulesRepository,
  readBranchRulesEvidence,
} from './IntegrationConfig_GitHub_BranchRules';

//
// Branch rules (PLAN-FBL-003): a branch name only; force pushes must be blocked on it (CIS 1.1.16)
//
export class ForcePushRule extends GitHubBranchRule {
  clone(): this {
    const rule = new ForcePushRule();
    rule.branch = this.branch;

    return rule as this;
  }

  isEqual(other: this): boolean {
    return this.branch.trim() === other.branch.trim();
  }
}

//
// Config class: the API's GitHub_ForcePushDisabledConfig
//
export class IntegrationConfig_GitHub_ForcePushDisabled extends GitHubBranchRulesConfig<ForcePushRule> {
  defaultRules: ForcePushRule[] = [new ForcePushRule()];

  protected newRule(): ForcePushRule {
    return new ForcePushRule();
  }

  static from(stored: unknown): IntegrationConfig_GitHub_ForcePushDisabled {
    const config = new IntegrationConfig_GitHub_ForcePushDisabled();
    config.readBranchRules((stored ?? {}) as Parameters<typeof config.readBranchRules>[0]);

    return config;
  }
}

//The problem codes of the API's GitHub_ForcePushDisabledHandler, stable in the evidence
export const getForcePushProblemText = (code: string, t: TFunction<string[]>): string => {
  switch (code) {
    case 'ForcePushAllowed':
      return t('integrations:GitHub_ForcePushDisabled.Problems.ForcePushAllowed');
    case 'PlanNotSupported':
      return t('integrations:GitHub_ForcePushDisabled.Problems.PlanNotSupported');
    case 'BranchNotFound':
      return t('integrations:GitHub_ForcePushDisabled.Problems.BranchNotFound');
    default:
      return getBranchRulesRepositoryProblemText(code, t);
  }
};

//What blocks force pushes on the branch: classic branch protection, a ruleset, both or nothing
export const getForcePushBlockedByText = (blockedBy: string, t: TFunction<string[]>): string => {
  switch (blockedBy) {
    case 'Classic':
      return t('integrations:GitHub_ForcePushDisabled.BlockedBy.Classic');
    case 'Ruleset':
      return t('integrations:GitHub_ForcePushDisabled.BlockedBy.Ruleset');
    case 'Both':
      return t('integrations:GitHub_ForcePushDisabled.BlockedBy.Both');
    default:
      return t('integrations:GitHub_ForcePushDisabled.BlockedBy.None');
  }
};

//
// Evidence classes: repository > rule > branch
//
export class ForcePushBranchResult extends GitHubBranchResult {
  //Classic, Ruleset, Both or None
  blockedBy: string = 'None';

  getFoundText(t: TFunction<string[]>): string {
    return getForcePushBlockedByText(this.blockedBy, t);
  }
}

export type ForcePushRuleResult = GitHubBranchRuleResult<ForcePushBranchResult>;

export type GitHub_ForcePushDisabledRepository = GitHubBranchRulesRepository<ForcePushRuleResult>;

export class GitHub_ForcePushDisabledEvidence extends GitHubBranchRulesEvidence<ForcePushRuleResult> {
  toMarkdownReport(t: TFunction<['translation', 'integrations']>): string {
    const typedT = t as TFunction<string[]>;

    return getBranchRulesMarkdownReport<ForcePushBranchResult>(
      this,
      t('integrations:GitHub_ForcePushDisabled.Report.SummaryHeading', {
        organization: this.organization,
        date: toLocaleDateTime(this.evaluatedAtUtc),
      }),
      (code) => getForcePushProblemText(code, typedT),
      (branch) => branch.getFoundText(typedT),
      typedT,
    );
  }
}

//The nested shape only; a result from before it shows as "test again"
export const parse_Evidence_GitHub_ForcePushDisabled = (
  raw: string | undefined,
): GitHub_ForcePushDisabledEvidence | undefined =>
  readBranchRulesEvidence(
    new GitHub_ForcePushDisabledEvidence(),
    raw,
    (item) => {
      const branch = new ForcePushBranchResult();
      branch.blockedBy = typeof item.blockedBy === 'string' ? item.blockedBy : 'None';

      return branch;
    },
    () => new GitHubBranchRuleResult<ForcePushBranchResult>(),
  );
